/**
 * PolygonOverlay - Read-only overlay for displaying polygons on PDF pages
 *
 * This is a lightweight, read-only alternative to PolygonManager (which uses Fabric.js).
 * Used for public review pages where editing is not allowed.
 */

import { useMemo } from 'react';
import type { ReviewPolygon } from '@/types/document-review';
import { cx } from '@/utils/cx';

interface PolygonOverlayProps {
    pageNumber: number;
    pageWidth: number;
    pageHeight: number;
    polygons: ReviewPolygon[];
    selectedPolygonId: string | null;
    onSelectPolygon?: (polygonId: string) => void;
    readOnly?: boolean;
}

export function PolygonOverlay({
    pageNumber,
    pageWidth,
    pageHeight,
    polygons,
    selectedPolygonId,
    onSelectPolygon,
    readOnly: _readOnly = false,
}: PolygonOverlayProps) {
    // Filter polygons for current page
    const pagePolygons = useMemo(() => {
        return polygons.filter((p) => p.pageNumber === pageNumber);
    }, [polygons, pageNumber]);

    // Get border color based on polygon status
    const getBorderColor = (polygon: ReviewPolygon): string => {
        if (polygon.status === 'solved' || polygon.attributes?.resolved === 'true') {
            return '#10B981'; // success green
        }
        if (polygon.priority === 'critical' || polygon.priority === 'high') {
            return '#EF4444'; // error red
        }
        return '#F59E0B'; // warning amber
    };

    if (pageWidth <= 0 || pageHeight <= 0) {
        return null;
    }

    return (
        <div className="absolute inset-0 pointer-events-none">
            {pagePolygons.map((polygon) => {
                const isSelected = selectedPolygonId === polygon.id;
                const borderColor = getBorderColor(polygon);

                // Convert normalized coordinates to pixels
                const left = polygon.x * pageWidth;
                const top = polygon.y * pageHeight;
                const width = polygon.width * pageWidth;
                const height = polygon.height * pageHeight;

                return (
                    <div
                        key={polygon.id}
                        className={cx(
                            'absolute pointer-events-auto cursor-pointer transition-all duration-150',
                            isSelected ? 'z-10' : 'z-0'
                        )}
                        style={{
                            left: `${left}px`,
                            top: `${top}px`,
                            width: `${width}px`,
                            height: `${height}px`,
                            border: `2px solid ${borderColor}`,
                            backgroundColor: isSelected
                                ? `${borderColor}20`
                                : 'transparent',
                            boxShadow: isSelected
                                ? `0 0 0 2px ${borderColor}40`
                                : 'none',
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onSelectPolygon?.(polygon.id);
                        }}
                        title={polygon.name || `Issue #${polygon.id.substring(0, 8)}`}
                    >
                        {/* Label badge */}
                        {polygon.name && (
                            <div
                                className="absolute -top-6 left-0 px-1.5 py-0.5 text-[10px] font-medium text-white rounded whitespace-nowrap max-w-[150px] truncate"
                                style={{ backgroundColor: borderColor }}
                            >
                                {polygon.name}
                            </div>
                        )}

                        {/* Selection indicator corners */}
                        {isSelected && (
                            <>
                                <div
                                    className="absolute -top-1 -left-1 w-2 h-2 rounded-full"
                                    style={{ backgroundColor: borderColor }}
                                />
                                <div
                                    className="absolute -top-1 -right-1 w-2 h-2 rounded-full"
                                    style={{ backgroundColor: borderColor }}
                                />
                                <div
                                    className="absolute -bottom-1 -left-1 w-2 h-2 rounded-full"
                                    style={{ backgroundColor: borderColor }}
                                />
                                <div
                                    className="absolute -bottom-1 -right-1 w-2 h-2 rounded-full"
                                    style={{ backgroundColor: borderColor }}
                                />
                            </>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export default PolygonOverlay;
